<script lang="ts">
    import type {
        AddExampleAction,
        ExampleAddedCarrier,
    } from "@app/models/api";
    import { sendCurrentDictionaryAnalyticsEvent } from "@app/services/analytics/analytics-send-event";
    import { dictionary } from "@app/services/currentDictionary.service";
    import { FetchError } from "@app/services/FetchError";
    import { addExample } from "@app/services/glosbe-dictionary-api/add-example";
    import {
        CHANGES__EXAMPLE_DETAILS,
        CHANGES__EXAMPLE_IT_USUALLY_TAKES_FEW_HOURS_TO_INDEX,
        CHANGES__EXAMPLE_PAIR_ADD,
        FORM__Add,
        FORM__Cancel,
        FORM__logInToAddEntries,
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import { isLoggedIn } from "@app/services/transferState-service";
    import { slide } from "svelte/transition";
    import { addFloatingMessage } from "../FloatingMessages/FloatingMessages.controller";
    import InlineMessage from "../InlineMessage.svelte";
    import LoadingInProgress from "../LoadingInProgress.svelte";
    import LogInFirst from "../LogInFirst.svelte";
    import Spinner from "../Spinner.svelte";
    import TextareaAutogrow from "../TextareaAutogrow.svelte";
    import AddExampleFormHeader from "./AddExampleFormHeader.svelte";

    export let onClose: () => void;
    export let open: boolean = false;

    const addExampleAction: AddExampleAction = {
        language1: $dictionary.l1.code,
        language2: $dictionary.l2.code,
        sentence1: "",
        sentence2: "",
    };

    type FORM_STATE = "ready" | "waiting" | "error" | "success";
    let formState: FORM_STATE = "ready";
    let errorMessage: string | undefined;

    async function onExampleAdded(addedExampleInfo: ExampleAddedCarrier) {
        formState = "success";
        addExampleAction.sentence1 = "";
        addExampleAction.sentence2 = "";
        addFloatingMessage({
            title: getMessage(CHANGES__EXAMPLE_PAIR_ADD),
            body: getMessage(
                CHANGES__EXAMPLE_IT_USUALLY_TAKES_FEW_HOURS_TO_INDEX
            ),
            actions: [
                {
                    text: getMessage(CHANGES__EXAMPLE_DETAILS),
                    linkUrl: `https://app.glosbe.com${addedExampleInfo.link}`,
                },
            ],
            type: "info",
        });

        setTimeout(() => {
            formState = "ready";
        }, 5000);
    }

    async function onSubmit(event: Event) {
        event.preventDefault();

        try {
            let addedExampleInfo: ExampleAddedCarrier = undefined;
            formState = "waiting";
            setTimeout(async () => {
                addedExampleInfo = await addExample(addExampleAction);
                sendCurrentDictionaryAnalyticsEvent({
                    action: "add-example",
                    label: "page-translation",
                });
                await onExampleAdded(addedExampleInfo);
            }, 5000);
        } catch (e: any) {
            errorMessage = "" + e;
            formState = "error";
            if (e instanceof FetchError) {
                const fetchError = e as FetchError;
                const errorCodes: string[] | undefined =
                    fetchError.json?.errorCodes;
                if (errorCodes) {
                    if (
                        errorCodes.indexOf(
                            "org.springframework.security.access.AccessDeniedException"
                        ) >= 0
                    ) {
                        errorMessage = getMessage(FORM__logInToAddEntries);
                    } else if (fetchError.json?.message) {
                        errorMessage = fetchError.json.message;
                    }
                }
            }
        }
    }
</script>

{#if open}
    <div
        transition:slide
        class="border-t border-b border-gray-300 bg-gray-100 my-2"
    >
        {#if isLoggedIn()}
            <AddExampleFormHeader {onClose} />
            {#if errorMessage}
                <div class="p-2">
                    <InlineMessage
                        messageType="error"
                        messageText={errorMessage}
                    />
                </div>
            {/if}
            <form
                on:submit={onSubmit}
                class="text-sm text-gray-900 py-2 bg-gray-50"
            >
                <div class="flex text-center text-sm text-gray-700 font-medium">
                    <div class="w-1/2">{$dictionary.l1.name}</div>
                    <div class="w-1/2">{$dictionary.l2.name}</div>
                </div>
                <div class="mb-2 p-2">
                    <div class="flex">
                        <div class="w-1/2 p-2">
                            <TextareaAutogrow
                                clasz="p-1 border border-primary-400 bg-white w-full rounded"
                                maxlength={500}
                                spellcheck={false}
                                autocorrect="off"
                                autocomplete="off"
                                autocapitalize="none"
                                placeholder="sentence 1"
                                minlength={5}
                                dir={$dictionary.l1.rtl ? "rtl" : "ltr"}
                                required
                                bind:value={addExampleAction.sentence1}
                            />
                        </div>

                        <div class="w-1/2 p-2">
                            <TextareaAutogrow
                                clasz="p-1 border border-primary-400 bg-white w-full rounded"
                                maxlength={500}
                                spellcheck={false}
                                autocorrect="off"
                                autocomplete="off"
                                autocapitalize="none"
                                placeholder="sentence 2"
                                minlength={5}
                                dir={$dictionary.l2.rtl ? "rtl" : "ltr"}
                                required
                                bind:value={addExampleAction.sentence2}
                            />
                        </div>
                    </div>

                    {#if errorMessage}
                        <div class="p-2">
                            <InlineMessage
                                messageType="error"
                                messageText={errorMessage}
                            />
                        </div>
                    {/if}
                    <div class="text-right p-2">
                        <button
                            type="button"
                            class="button-sm"
                            on:click={onClose}
                            >{getMessage(FORM__Cancel)}
                        </button>
                        <button
                            type="submit"
                            class="button-sm"
                            class:button-primary={formState === "ready"}
                            disabled={formState !== "ready"}
                        >
                            {#if formState === "ready"}
                                {getMessage(FORM__Add)}
                            {/if}

                            {#if formState === "success"}
                                {getMessage(CHANGES__EXAMPLE_PAIR_ADD)}
                            {/if}
                            {#if formState === "waiting"}
                                <Spinner size="1rem" />
                            {/if}
                        </button>
                    </div>
                </div>
            </form>
        {:else}
            <div class="p-2">
                <LogInFirst />
            </div>
        {/if}
    </div>
{/if}
